import { Directive, DoCheck, inject, Input } from "@angular/core";
import { FormControl, FormGroupDirective, NgControl, NgForm, Validators } from "@angular/forms";
import { _ErrorStateTracker, ErrorStateMatcher } from "@angular/material/core";
import { Observable } from "rxjs/internal/Observable";
import { Subject } from "rxjs/internal/Subject";

/**
 * Provides common Boilerplate for a Custom-Form-Field-Implementation.
 *
 * Should only be extended by components that also implement `ControlValueAccessor` and `MatFormFieldControl`
 */
@Directive()
export class CustomFormFieldBase<FormControlType> implements DoCheck {
  /**
   * The FormControl of the custom form-field. Needs to be provided by the extending component.
   */
  protected formControl!: FormControl<FormControlType>;

  public readonly ngControl: NgControl | null = inject(NgControl, {
    optional: true,
    self: true
  });

  @Input()
  public set placeholder (placeholder: string) {
    this._placeholder = placeholder;
    this._stateChange$.next();
  }

  public get placeholder (): string {
    return this._placeholder;
  }

  private _placeholder: string = "";


  public get value (): FormControlType {
    return this.formControl.value;
  }

  public get empty (): boolean {
    return !this.formControl.value;
  }

  public get disabled (): boolean {
    return this.formControl.disabled;
  }

  public get errorState (): boolean {
    return this.errorStateTracker.errorState;
  }

  protected _stateChange$: Subject<void> = new Subject<void>();

  // Violates ogs-naming convention but needs to be implemented by form-field-interface
  public stateChanges: Observable<void> = this._stateChange$.asObservable();

  protected errorStateTracker!: _ErrorStateTracker;

  @Input()
  public set required (isRequired: boolean) {
    this._required = isRequired;
    this._stateChange$.next();
  }

  public get required (): boolean {
    // eslint-disable-next-line @tseslint/unbound-method
    return this._required || Boolean(this.ngControl?.control?.hasValidator(Validators.required));
  }

  private _required: boolean = false;


  constructor () {
    const parentForm: NgForm | null = inject(NgForm, { optional: true });
    const parentFormGroup: FormGroupDirective | null = inject(FormGroupDirective, { optional: true });
    const defaultErrorStateMatcher: ErrorStateMatcher = inject(ErrorStateMatcher);

    this.errorStateTracker = new _ErrorStateTracker(
      defaultErrorStateMatcher,
      this.ngControl,
      parentFormGroup,
      parentForm,
      this._stateChange$
    );
  }


  setDisabledState? (isDisabled: boolean): void {
    if (isDisabled)
      this.formControl.disable({ emitEvent: false });
    else
      this.formControl.enable({ emitEvent: false });
  }

  public writeValue (value: FormControlType): void {
    this.formControl.setValue(value, { emitEvent: false });
    this._stateChange$.next();
  }

  public ngDoCheck (): void {
    this.errorStateTracker.updateErrorState();
  }
}
